/**
 * `/create` (docs/CREATION-V2.md, docs/API.md "Creation v2"): token first, strategy in the desk.
 *
 *   1. Identity: the look (the 3D mascot, `LookStep`, with the name under it), the ticker (3-6 letters, suggested from
 *      the name, checked live with `GET /agents/ticker-available`), one paragraph on what the agent should do, and the
 *      chain (`ChainStep`, Robinhood Chain preselected). No strategy, no upload: the mascot is the image.
 *   2. Launch: `POST /agents/launch`. When the platform can launch the token (`GET /agents/launch-terms`), one wallet
 *      transaction (0.0005 ETH as value of `payCreation`) pays for the agent and its token launch; while it cannot,
 *      the agent is created free and without a token, and the page says so before anything is asked (FEE1). The
 *      agent exists in `setting_up` and its basics are drafted (`LaunchPanel`).
 *   3. Hand-off: "Continue in your desk" opens the agent's thread, where the basics card and the interview are.
 *
 * The identity and the launch's client key survive a reload of this tab (`lib/create-draft.ts`), so a retried launch
 * is the same launch; `?intent=` (a desk starter or a desk request) prefills the description; `?look=` (the Studio)
 * seeds the look.
 */
import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { suggestTicker, TICKER_PATTERN, type AgentAvatar } from '@algoboo/schema/api'
import { CREATION_FEE_ETH, DEFAULT_VENUE } from '@algoboo/schema/catalog'
import { Button, Icon, Input, Skeleton, Textarea, cx, defaultAvatarFor } from '@algoboo/ui'
import { errorCopy, isApiError, venuesQuery } from '../lib/api'
import { launchTermsQuery, useLaunch, useLaunchAgent, useLaunchTerms, useTickerAvailability } from '../lib/api/hooks/creation'
import { prefetch } from '../lib/route-data'
import { usePageMeta } from '../lib/meta'
import { useSession } from '../components/session'
import { decodeStudioLook, readStudio, STUDIO_STORAGE_KEY } from '../lib/agent-studio'
import { ChainStep } from '../components/create/ChainStep'
import { LookStep } from '../components/create/LookStep'
import { LaunchPanel } from '../components/create/LaunchPanel'
import { handleFromName, identityIssues } from '../components/create/identity'
import { clearCreateDraft, loadCreateDraft, saveCreateDraft, type IdentityDraft } from '../lib/create-draft'

export const Route = createFileRoute('/create')({
  validateSearch: (search: Record<string, unknown>): { look?: string; intent?: string } => {
    const avatar = decodeStudioLook(search['look'])
    // A description carried over from the desk (a starter or a request): it prefills step 1's description.
    const intent = typeof search['intent'] === 'string' ? search['intent'].trim().slice(0, 1000) : ''
    return { ...(avatar ? { look: JSON.stringify(avatar) } : {}), ...(intent ? { intent } : {}) }
  },
  // the chain step's venues and whether a launch is possible now (what creating costs)
  loader: ({ context: { queryClient: qc } }) => prefetch(qc.ensureQueryData(venuesQuery()), qc.ensureQueryData(launchTermsQuery())),
  component: CreatePage,
})

const newKey = () => String(globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`)
const RAIL = ['Identity', 'Launch', 'Set up in your desk'] as const

function useDebounced<T>(v: T, ms = 350): T {
  const [d, setD] = useState(v)
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms)
    return () => clearTimeout(t)
  }, [v, ms])
  return d
}

function savedStudioLook(): AgentAvatar | undefined {
  try {
    const storage = window.localStorage
    return storage.getItem(STUDIO_STORAGE_KEY) ? readStudio(storage).avatar : undefined
  } catch {
    return undefined
  }
}

function freshDraft(description: string, avatar: AgentAvatar): IdentityDraft {
  return { v: 2, name: '', ticker: '', description, avatar, venue: DEFAULT_VENUE, clientKey: newKey(), launchId: null }
}

/**
 * What the page promises, from the platform's own answer: with a token launch the fee pays for both; without one the
 * agent is free and the page names why there is no token yet. While the answer is loading nothing is promised.
 */
function useOffer() {
  const terms = useLaunchTerms()
  const launch = terms.data?.tokenLaunch.available ?? null
  const reason = terms.data?.tokenLaunch.reason ?? null
  return {
    loading: terms.isLoading,
    launch,
    reason,
    lede:
      launch === true
        ? `Its token launches with it: ${CREATION_FEE_ETH} ETH, token launch included. You set up its strategy together in your desk, and it trades in dry run first.`
        : launch === false
          ? `Creating an agent is free right now. Its token cannot launch yet (${reason ?? 'the platform does not launch agent tokens yet'}), so nothing is charged for it; you launch the token later from its page, for ${CREATION_FEE_ETH} ETH. It trades in dry run first.`
          : 'Checking whether its token can launch now…',
    cta:
      launch === true
        ? `One transaction: ${CREATION_FEE_ETH} ETH (about 1 to 2 USD), token launch included. It trades nothing until you approve its first strategy, and then in dry run.`
        : launch === false
          ? 'Free: no transaction and no token yet. It trades nothing until you approve its first strategy, and then in dry run.'
          : 'Checking the price…',
  }
}

function CreatePage() {
  usePageMeta('Create an agent', 'Name it, give it a ticker, a face and one paragraph. It sets up its strategy with you and starts in dry run.')
  const offer = useOffer()
  const { look, intent } = Route.useSearch()
  const lookAvatar = useMemo(() => decodeStudioLook(look) ?? savedStudioLook(), [look])
  const { me, signIn } = useSession()
  const [draft, setDraft] = useState<IdentityDraft>(() => {
    const saved = loadCreateDraft()
    if (saved) return intent && !saved.launchId ? { ...saved, description: intent } : saved
    return freshDraft(intent ?? '', lookAvatar ?? defaultAvatarFor(`new-agent-${newKey().slice(0, 6)}`))
  })
  const [tickerTouched, setTickerTouched] = useState(() => !!draft.ticker)
  const [tried, setTried] = useState(false)
  useEffect(() => saveCreateDraft(draft), [draft])

  // The ticker follows the name until the creator types one.
  const ticker = tickerTouched ? draft.ticker : draft.name.trim() ? suggestTicker(draft.name) : ''
  const debTicker = useDebounced(ticker)
  const debName = useDebounced(draft.name.trim())
  const avail = useTickerAvailability(debTicker, debName, TICKER_PATTERN.test(debTicker) && !draft.launchId)
  const tickerFree = avail.data && avail.data.ticker === ticker ? avail.data.available : null
  const issues = identityIssues({ ...draft, ticker }, tickerFree)
  const ready = Object.keys(issues).length === 0

  const launchQ = useLaunch(draft.launchId)
  const launch = useLaunchAgent((l) => setDraft((d) => ({ ...d, launchId: l.id })))
  const go = () => {
    setTried(true)
    if (!ready) return
    if (!me) {
      signIn('Sign in with the wallet that pays for and owns the agent.')
      return
    }
    setDraft((d) => ({ ...d, ticker }))
    launch.mutate({ body: { clientKey: draft.clientKey, name: draft.name.trim(), ticker, description: draft.description.trim(), avatar: draft.avatar, venue: draft.venue } })
  }
  const restart = () => {
    clearCreateDraft()
    launch.reset()
    setTried(false)
    setTickerTouched(false)
    setDraft(freshDraft('', defaultAvatarFor(`new-agent-${newKey().slice(0, 6)}`)))
  }
  const pickTicker = (t: string) => {
    setTickerTouched(true)
    setDraft((d) => ({ ...d, ticker: t }))
    launch.reset()
  }
  const launchErr = launch.error
  const takenSuggestion = launchErr && isApiError(launchErr) && launchErr.code === 'TICKER_TAKEN' ? ((launchErr.details as { suggestion?: string } | undefined)?.suggestion ?? null) : null

  const l = launchQ.data ?? launch.data ?? null
  const step = !draft.launchId ? 0 : l?.status === 'launched' ? 2 : 1

  return (
    <div className="mx-auto grid max-w-6xl items-start gap-6 lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-10" data-testid="create-page" data-phase={step === 0 ? 'identity' : 'launch'}>
      <aside className="lg:sticky lg:top-24 lg:self-start">
        <h1 className="font-display text-[30px] font-semibold leading-[1.05] tracking-tight text-ink">Launch an agent</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-3" data-testid="create-offer" data-launch={offer.launch === null ? 'unknown' : String(offer.launch)}>
          {offer.lede}
        </p>
        <ol className="mt-5 hidden space-y-0.5 lg:block" aria-label="Steps">
          {RAIL.map((label, i) => (
            <li key={label} aria-current={i === step ? 'step' : undefined} className={cx('flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px]', i === step ? 'font-semibold text-ink' : i < step ? 'text-ink-2' : 'text-ink-3')}>
              <span className={cx('flex h-5 w-5 items-center justify-center rounded-full font-mono text-[10.5px]', i < step ? 'bg-accent-fill text-on-accent' : i === step ? 'bg-ink text-ink-inverse' : 'bg-sunken')}>
                {i < step ? <Icon name="check" size={11} strokeWidth={2.6} /> : i + 1}
              </span>
              {label}
            </li>
          ))}
        </ol>
        {(draft.name || draft.launchId) && (
          <button type="button" onClick={restart} className="rh-press mt-4 text-[12.5px] font-medium text-ink-3 hover:text-ink" data-testid="create-restart">
            Start over
          </button>
        )}
      </aside>

      <div className="min-w-0 space-y-6">
        {draft.launchId ? (
          l ? (
            <LaunchPanel launch={l} onRestart={restart} />
          ) : launchQ.error ? (
            <p role="alert" className="rh-card p-5 text-[13.5px] text-warn">
              {isApiError(launchQ.error) ? errorCopy(launchQ.error.code).title : 'The launch could not be loaded.'}{' '}
              <button type="button" className="underline" onClick={restart}>
                Start over
              </button>
            </p>
          ) : (
            <Skeleton className="h-72 rounded-xl" />
          )
        ) : (
          <>
            <LookStep step="Step 1 · Identity" avatar={draft.avatar} onChange={(avatar) => setDraft((d) => ({ ...d, avatar }))} name={draft.name} handle={handleFromName(draft.name)} onName={(name) => setDraft((d) => ({ ...d, name }))} />

            <section aria-labelledby="create-token-title" className="rh-card space-y-5 p-5 sm:p-6" data-testid="create-identity">
              <div>
                <h2 id="create-token-title" className="text-[20px] font-semibold tracking-tight text-ink">
                  Its token and what it should do
                </h2>
                <p className="mt-1.5 max-w-prose text-[13.5px] leading-relaxed text-ink-2">The ticker is the token&apos;s symbol. The description is all the agent needs to start: it drafts its basics from it and asks you a few short questions in your desk.</p>
              </div>
              <div className="max-w-xs">
                <label htmlFor="create-ticker" className="block text-[12.5px] font-medium text-ink-2">
                  Ticker
                </label>
                <Input
                  id="create-ticker"
                  value={ticker}
                  maxLength={6}
                  prefix="$"
                  placeholder="MOON"
                  invalid={tried && !!issues.ticker}
                  onChange={(e) => pickTicker(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6))}
                  aria-describedby="create-ticker-note"
                  data-testid="create-ticker"
                />
                <p id="create-ticker-note" className="mt-1 min-h-[18px] text-[12px]" data-testid="ticker-status" data-available={tickerFree === null ? 'unknown' : String(tickerFree)}>
                  {!ticker ? (
                    <span className="text-ink-3">3 to 6 letters; it follows the name until you type one.</span>
                  ) : !TICKER_PATTERN.test(ticker) ? (
                    <span className="text-ink-3">3 to 6 capital letters.</span>
                  ) : tickerFree === true ? (
                    <span className="text-gain">${ticker} is free.</span>
                  ) : tickerFree === false ? (
                    <span className="text-warn">
                      ${ticker} is taken.{' '}
                      {avail.data?.suggestion && (
                        <button type="button" className="font-medium underline" onClick={() => pickTicker(avail.data!.suggestion!)} data-testid="ticker-suggestion">
                          Use ${avail.data.suggestion}
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="text-ink-3">Checking ${ticker}…</span>
                  )}
                </p>
              </div>
              <div>
                <label htmlFor="create-description" className="block text-[12.5px] font-medium text-ink-2">
                  What should this agent do?
                </label>
                <Textarea
                  id="create-description"
                  rows={5}
                  maxLength={1000}
                  value={draft.description}
                  invalid={tried && !!issues.description}
                  placeholder="Buy fresh Robinhood Chain launches that show real buyer growth, keep sizes small and cut losers fast."
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  data-testid="create-description"
                />
                <p className="mt-1 flex justify-between gap-3 text-[12px] text-ink-3">
                  <span>{tried && issues.description ? <span className="text-warn">{issues.description}</span> : 'Plain words. Tokens, how bold, when to get out: whatever you already know.'}</span>
                  <span className="num shrink-0 font-mono">{draft.description.length}/1000</span>
                </p>
              </div>
            </section>

            <ChainStep value={draft.venue} onChange={(venue) => setDraft((d) => ({ ...d, venue }))} />

            <section className="rh-card flex flex-wrap items-center gap-4 p-5 sm:p-6" data-testid="create-launch-cta">
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-ink">
                  {offer.launch === false ? 'Create' : 'Launch'} {draft.name.trim() || 'your agent'}
                  {offer.launch === false ? '' : TICKER_PATTERN.test(ticker) ? ` and $${ticker}` : ''}
                </p>
                <p className="mt-0.5 text-[12.5px] text-ink-3">{offer.cta}</p>
                {tried && !ready && (
                  <p role="alert" className="mt-1.5 text-[12.5px] text-warn" data-testid="create-issues">
                    {Object.values(issues).join(' ')}
                  </p>
                )}
                {launchErr && (
                  <p role="alert" className="mt-1.5 text-[12.5px] text-warn" data-testid="create-launch-error">
                    {isApiError(launchErr) ? `${errorCopy(launchErr.code).title}: ${launchErr.message}` : String(launchErr)}
                    {takenSuggestion && (
                      <>
                        {' '}
                        <button type="button" className="font-medium underline" onClick={() => pickTicker(takenSuggestion)}>
                          Use ${takenSuggestion}
                        </button>
                      </>
                    )}
                  </p>
                )}
              </div>
              <Button variant="primary" icon="bolt" loading={launch.isPending} disabled={offer.loading} onClick={go} data-testid="create-launch-button">
                {offer.launch === false ? (me ? 'Create for free' : 'Connect and create') : me ? 'Launch' : 'Connect and launch'}
              </Button>
            </section>
          </>
        )}
      </div>
    </div>
  )
}
